import SysColumn from "app/class/sys-column";
import { parseStartInputs } from "modules/fluss/utils/startInputs";
import { FlussElementSelection } from "modules/fluss/types/fluss";
import { FieldMapping, FlussValue, ScopeOption } from "../fluss-builder-types";
import type { FlussFlowGraph, FlussFlowGraphNodeConfig } from "modules/fluss/services/fluss-api";

export type FlussPanelNodeKind =
    | "start"
    | "user_task"
    | "manual_task"
    | "approval"
    | "service_task"
    | "script_task"
    | "business_rule_task"
    | "send_task"
    | "signal_start"
    | "message_start"
    | "message_catch"
    | "signal_catch"
    | "signal_throw"
    | "signal_end"
    | "link_throw"
    | "link_catch"
    | "timer_start"
    | "timer_catch"
    | "boundary_timer"
    | "boundary_error"
    | "boundary_escalation"
    | "boundary_signal"
    | "boundary_conditional"
    | "conditional_catch"
    | "exclusive_gateway"
    | "parallel_gateway"
    | "inclusive_gateway"
    | "event_based_gateway"
    | "end"
    | "terminate_end"
    | "error_end"
    | "escalation_throw"
    | "milestone"
    | "subprocess"
    | "event_subprocess"
    | "event_start_timer"
    | "event_start_signal"
    | "event_start_conditional"
    | "event_start_error"
    | "event_start_escalation"
    | "call_activity"
    | "data_store_reference"
    | "data_object_reference"
    | "unsupported";

export const ACTIONS_RESERVED_FOR_NODE_TYPE: Record<string, FlussPanelNodeKind> = {
    send_email: "send_task",
};

export const factoryActionsForNodeKind = <T extends { name: string; active?: boolean }>(actions: T[], kind: FlussPanelNodeKind) =>
    actions.filter((action) => {
        const reservedFor = ACTIONS_RESERVED_FOR_NODE_TYPE[action.name];
        return action.active !== false && (!reservedFor || reservedFor === kind);
    });

export const sortFactoryActions = <T extends { name: string; order?: number | null }>(actions: T[]) =>
    [...actions].sort((left, right) => {
        const leftOrder = left.order ?? Number.POSITIVE_INFINITY;
        const rightOrder = right.order ?? Number.POSITIVE_INFINITY;
        return leftOrder - rightOrder || left.name.localeCompare(right.name);
    });

export interface FlussBindingLike {
    $type?: string;
    kind?: string;
    [key: string]: unknown;
}

const extensionValues = (businessObject: any): FlussBindingLike[] =>
    businessObject?.extensionElements?.values || [];

export const bindingsFor = (businessObject: any) =>
    extensionValues(businessObject).filter((value) => value.$type === "fluss:Binding");

export const primaryBindingFor = (businessObject: any) =>
    bindingsFor(businessObject).find((value) => value.kind !== "notification");

export const bindingForKind = (businessObject: any, kind: string) =>
    bindingsFor(businessObject).find((value) => value.kind === kind);

const eventDefinitionType = (businessObject: any) =>
    businessObject?.eventDefinitions?.[0]?.$type || "";

export const MESSAGE_START_EVENT_TYPE = "custom.email.inbound_received";

export const messageBindingFor = (businessObject: any) => bindingForKind(businessObject, "message");

export const messageEventTypeFor = (businessObject: any) =>
    String(messageBindingFor(businessObject)?.eventType || MESSAGE_START_EVENT_TYPE);

export const signalEventTypeFor = (businessObject: any) => {
    const binding = bindingForKind(businessObject, "signal");
    return String(
        binding?.eventType ||
        binding?.signalName ||
        businessObject?.eventDefinitions?.[0]?.signalRef?.name ||
        ""
    );
};

export const updateSignalEventType = (
    selection: FlussElementSelection,
    modeler: any,
    eventType: string
) => {
    const modeling = modeler?.get("modeling");
    const moddle = modeler?.get("moddle");
    const eventDefinition = selection.businessObject?.eventDefinitions?.[0];
    if (!modeling || !moddle || !eventDefinition) return;

    let definitions = selection.businessObject?.$parent;
    while (definitions && definitions.$type !== "bpmn:Definitions") definitions = definitions.$parent;
    let signal = eventDefinition.signalRef;
    if (!signal && definitions) {
        signal = (definitions.rootElements || []).find(
            (candidate: any) => candidate.$type === "bpmn:Signal" && candidate.name === eventType
        );
        if (!signal) {
            const suffix = eventType.replace(/[^A-Za-z0-9_]/g, "_") || selection.id;
            signal = moddle.create("bpmn:Signal", {
                id: `Signal_${suffix}`,
                name: eventType,
            });
            modeling.updateModdleProperties(selection.element, definitions, {
                rootElements: [...(definitions.rootElements || []), signal],
            });
        }
        modeling.updateModdleProperties(selection.element, eventDefinition, { signalRef: signal });
    } else if (signal) {
        modeling.updateModdleProperties(selection.element, signal, { name: eventType });
    }

    updateBinding(selection, modeler, "signal", {
        eventType,
        signalName: eventType,
    });
};

// Fase 11 (A5): an event sub-process's internal start shares its BPMN tag (bpmn:StartEvent) and
// event definitions with every OTHER start/catch/boundary variant already handled below — the
// ONLY thing that distinguishes it structurally is living directly inside a
// <bpmn:subProcess triggeredByEvent="true">. Must be checked before the generic
// signal/timer-event-definition branches, or a timer-typed event sub-process start would be
// misread as a plain top-level "timer_start".
const eventSubprocessStartTypeFor = (selection: FlussElementSelection): FlussPanelNodeKind | null => {
    if (selection.type !== "bpmn:StartEvent") return null;
    const parent = selection.businessObject?.$parent;
    if (parent?.$type !== "bpmn:SubProcess" || !parent?.triggeredByEvent) return null;

    const definitionType = eventDefinitionType(selection.businessObject);
    if (definitionType === "bpmn:TimerEventDefinition") return "event_start_timer";
    if (definitionType === "bpmn:SignalEventDefinition") return "event_start_signal";
    if (definitionType === "bpmn:ConditionalEventDefinition") return "event_start_conditional";
    if (definitionType === "bpmn:ErrorEventDefinition") return "event_start_error";
    if (definitionType === "bpmn:EscalationEventDefinition") return "event_start_escalation";
    return null;
};

export const panelNodeKindFor = (selection: FlussElementSelection | null): FlussPanelNodeKind => {
    if (!selection) return "unsupported";
    const type = selection.type;
    const bindingKind = String(primaryBindingFor(selection.businessObject)?.kind || "");
    const definitionType = eventDefinitionType(selection.businessObject);
    const eventSubprocessStartType = eventSubprocessStartTypeFor(selection);
    if (eventSubprocessStartType) return eventSubprocessStartType;

    const primaryBinding = primaryBindingFor(selection.businessObject);
    if (type === "bpmn:UserTask") return bindingKind === "approval" ? "approval" : "user_task";
    if (type === "bpmn:BusinessRuleTask") return "business_rule_task";
    if (type === "bpmn:ScriptTask") return "script_task";
    if (type === "bpmn:ServiceTask" && primaryBinding?.businessRule && !primaryBinding?.actionId) return "business_rule_task";
    if (type === "bpmn:ManualTask") return "manual_task";
    if (type === "bpmn:ServiceTask") return "service_task";
    if (type === "bpmn:SendTask") return "send_task";
    if (type === "bpmn:CallActivity") return "call_activity";
    if (type === "bpmn:ExclusiveGateway") return "exclusive_gateway";
    if (type === "bpmn:ParallelGateway") return "parallel_gateway";
    if (type === "bpmn:InclusiveGateway") return "inclusive_gateway";
    if (type === "bpmn:EventBasedGateway") return "event_based_gateway";
    if (type === "bpmn:SubProcess") return selection.businessObject?.triggeredByEvent ? "event_subprocess" : "subprocess";
    if (type === "bpmn:DataStoreReference") return "data_store_reference";
    if (type === "bpmn:DataObjectReference") return "data_object_reference";
    if (type === "bpmn:EndEvent" && definitionType === "bpmn:ErrorEventDefinition") return "error_end";
    if (type === "bpmn:IntermediateThrowEvent" && definitionType === "bpmn:EscalationEventDefinition") return "escalation_throw";
    if (definitionType === "bpmn:MessageEventDefinition") {
        if (type === "bpmn:StartEvent") return "message_start";
        if (type === "bpmn:IntermediateCatchEvent") return "message_catch";
    }
    if (definitionType === "bpmn:SignalEventDefinition") {
        if (type === "bpmn:StartEvent") return "signal_start";
        if (type === "bpmn:IntermediateCatchEvent") return "signal_catch";
        if (type === "bpmn:IntermediateThrowEvent") return "signal_throw";
        if (type === "bpmn:EndEvent") return "signal_end";
    }
    if (definitionType === "bpmn:TimerEventDefinition") {
        if (type === "bpmn:StartEvent") return "timer_start";
        if (type === "bpmn:IntermediateCatchEvent") return "timer_catch";
        if (type === "bpmn:BoundaryEvent") return "boundary_timer";
    }
    if (definitionType === "bpmn:ConditionalEventDefinition" && type === "bpmn:IntermediateCatchEvent") return "conditional_catch";
    if (definitionType === "bpmn:LinkEventDefinition") {
        if (type === "bpmn:IntermediateThrowEvent") return "link_throw";
        if (type === "bpmn:IntermediateCatchEvent") return "link_catch";
    }
    if (type === "bpmn:BoundaryEvent") {
        if (definitionType === "bpmn:EscalationEventDefinition") return "boundary_escalation";
        if (definitionType === "bpmn:ErrorEventDefinition") return "boundary_error";
        if (definitionType === "bpmn:SignalEventDefinition") return "boundary_signal";
        if (definitionType === "bpmn:ConditionalEventDefinition") return "boundary_conditional";
    }
    if (type === "bpmn:EndEvent" && definitionType === "bpmn:TerminateEventDefinition") {
        return "terminate_end";
    }
    if (type === "bpmn:IntermediateThrowEvent") return "milestone";
    if (type === "bpmn:StartEvent") return "start";
    if (type === "bpmn:EndEvent") return "end";
    return "unsupported";
};

export const nodeTypeLabelKey = (kind: FlussPanelNodeKind) => `fluss.node_panel.type.${kind}`;

// PRIM-PILOT-072 (slice 1B-4a): mirrors Fluss::Compiler::SUPPORTED (compiler.rb:20-34) plus its
// user_task -> approval override (compiler.rb:200), NOT panelNodeKindFor above. panelNodeKindFor
// speaks the Studio panel's own vocabulary (e.g. "business_rule_task" for a bpmn:ServiceTask with
// a Business Rule binding), which Fluss::AvailableVariables::SERVICE_NODE_TYPES
// (%w[service_task script_task send_task]) does not recognize -- feeding it into the flow graph
// sent to /available_variables would silently zero out every Business Rule node's outputs. This
// function speaks the Compiler's runtime vocabulary instead, the one the backend actually
// understands. Types the Compiler doesn't map (gateways, events, start/end, ...) return "" -- the
// backend's Fluss::AvailableVariables simply skips nodes whose type isn't in its own list, so an
// empty string is inert, never an error.
export const runtimeNodeTypeFor = (businessObject: any): string => {
    const type = businessObject?.$type;
    const eventType = businessObject?.eventDefinitions?.[0]?.$type;
    if (type === "bpmn:IntermediateCatchEvent" && eventType === "bpmn:MessageEventDefinition") return "message_catch";
    if (eventType === "bpmn:TimerEventDefinition") {
        if (type === "bpmn:IntermediateCatchEvent") return "timer_catch";
        if (type === "bpmn:BoundaryEvent") return "boundary_timer";
        if (type === "bpmn:StartEvent") return "timer_start";
    }
    const bindingKind = String(primaryBindingFor(businessObject)?.kind || "");
    if (type === "bpmn:UserTask") return bindingKind === "approval" ? "approval" : "user_task";
    if (type === "bpmn:BusinessRuleTask") return "service_task";
    if (type === "bpmn:ServiceTask") return "service_task";
    if (type === "bpmn:ScriptTask") return "script_task";
    if (type === "bpmn:SendTask") return "send_task";
    if (type === "bpmn:ManualTask") return "manual_task";
    if (type === "bpmn:StartEvent") return "start";
    if (type === "bpmn:EndEvent") return "end";
    if (type === "bpmn:ExclusiveGateway") return "exclusive_gateway";
    if (type === "bpmn:ParallelGateway") return "parallel_gateway";
    if (type === "bpmn:InclusiveGateway") return "inclusive_gateway";
    if (type === "bpmn:EventBasedGateway") return "event_based_gateway";
    if (type === "bpmn:CallActivity") return "call_activity";
    return "";
};

// PRIM-PILOT-072 (slice 1B-4a): whitelist mirroring the architecture_decision exactly -- only
// actionId, params, businessRule and variableMappings ever leave this function, the same 4 keys
// Fluss::AvailableVariables actually reads off a live bpmn-js binding (a fluss:Binding moddle
// object only ever carries the camelCase attribute names; the snake_case variants on
// FlussFlowGraphNodeConfig/the backend permit exist for the persisted-snapshot/API-client shape,
// not this live-canvas one). No other binding key is ever forwarded, deliberately -- the same
// whitelist gap the backend permit had before PRIM-PILOT-071 (1B-3b) must not reappear here.
const flowGraphConfigFor = (businessObject: any): FlussFlowGraphNodeConfig | undefined => {
    const binding = primaryBindingFor(businessObject) as Record<string, unknown> | undefined;
    if (!binding) return undefined;
    const config: FlussFlowGraphNodeConfig = {};
    if (binding.actionId !== undefined) config.actionId = String(binding.actionId);
    if (binding.businessRule !== undefined) config.businessRule = String(binding.businessRule);
    if (binding.params !== undefined) config.params = binding.params as Record<string, unknown>;
    if (binding.variableMappings !== undefined) {
        const parsed = parseMapping(binding.variableMappings);
        config.variableMappings = Object.fromEntries(parsed.map((row) => [row.target, row.source]));
    }
    // PRIM-PILOT-085 (slice 1F-2): target_table/field_mappings/formId are the canonical keys the
    // Studio panel writes on a user_task/approval binding (ActivityNodePanels.tsx) -- snake_case
    // for the first two, camelCase for formId; targetTable/fieldMappings are read as legacy
    // fallbacks only. field_mappings is passed through as the raw JSON string: Fluss::WorkRecordBinding
    // is the one canonical parser (available_variables.rb), so re-parsing it here would be a
    // second implementation of the same contract with no benefit.
    const targetTable = binding.target_table ?? binding.targetTable;
    if (targetTable !== undefined) config.target_table = String(targetTable);
    const fieldMappings = binding.field_mappings ?? binding.fieldMappings;
    if (fieldMappings !== undefined) config.field_mappings = fieldMappings as string | Record<string, unknown>;
    const formId = binding.formId ?? binding.form_id;
    if (formId !== undefined) config.formId = String(formId);
    const formMode = binding.formMode ?? binding.form_mode;
    if (formMode !== undefined) config.formMode = String(formMode);
    // Fluss::AvailableVariables#outcome_typing needs this to know a condition-driven user_task
    // (Fluss::UserTaskWaitBinding.wait_for_completion?) has no outcome vocabulary to offer --
    // without it, every ancestor user_task looks non-condition-driven from here, and the
    // condition builder would wrongly offer "complete" as a CHOICE for one that actually
    // completes with outcome: nil.
    if (binding.waitForCompletion !== undefined) config.waitForCompletion = binding.waitForCompletion as string | boolean;
    if (binding.table !== undefined) config.table = String(binding.table);
    if (binding.triggerType !== undefined) config.triggerType = String(binding.triggerType);
    if (binding.initialVariables !== undefined) {
        config.initialVariables = Object.fromEntries(parseMapping(binding.initialVariables).map((row) => [row.target, row.source]));
    }
    if (binding.startInputs !== undefined) config.startInputs = parseStartInputs(binding.startInputs);
    if (binding.definitionKey !== undefined) config.definitionKey = String(binding.definitionKey);
    if (binding.versionPolicy !== undefined) config.versionPolicy = String(binding.versionPolicy);
    if (binding.pinnedVersion !== undefined) config.pinnedVersion = String(binding.pinnedVersion);
    return Object.keys(config).length > 0 ? config : undefined;
};

// PRIM-PILOT-072 (slice 1B-4a): builds the FlussFlowGraph payload for
// fetchDataCatalog straight from the Studio's live canvas (bpmn-js elementRegistry), NOT
// the persisted snapshot -- same reason fetchDataCatalog's own docstring gives
// (fluss-api.ts): the backend has no other way to see unsaved edits. Same defensive guard
// as resolveAnchorTable/linkNamesInDiagram: any missing modeler/registry
// resolves to an empty graph, never throws.
export const buildFlowGraph = (modeler: any): FlussFlowGraph => {
    const elementRegistry = typeof modeler?.get === "function" ? modeler.get("elementRegistry") : null;
    const elements: any[] = typeof elementRegistry?.getAll === "function" ? elementRegistry.getAll() : [];

    const nodes: FlussFlowGraph["nodes"] = [];
    const edges: FlussFlowGraph["edges"] = [];

    for (const element of elements) {
        // bpmn-js keeps a separate diagram-js element for a shape's external label (small shapes
        // like start/end events and gateways render their label outside the shape; tasks don't,
        // since their label lives inside the shape itself). That pseudo-element shares the SAME
        // businessObject as the shape it labels -- without this guard every labelled shape is
        // pushed into `nodes` twice. Only the backend's Hash-based dedupe by id
        // (available_variables.rb) was masking this; the frontend must not rely on it.
        if (element?.type === "label" || element?.labelTarget) continue;

        const businessObject = element?.businessObject;
        if (!businessObject) continue;

        if (businessObject.$type === "bpmn:SequenceFlow") {
            const sourceId = businessObject.sourceRef?.id;
            const targetId = businessObject.targetRef?.id;
            if (sourceId && targetId) edges.push({ source: sourceId, target: targetId });
            continue;
        }

        const id = businessObject.id || element.id;
        if (!id) continue;
        nodes.push({
            id,
            type: runtimeNodeTypeFor(businessObject),
            label: String(businessObject.name || ""),
            config: flowGraphConfigFor(businessObject),
        });
    }

    return { nodes, edges };
};

export const linkNameFor = (businessObject: any): string => {
    const elementName = String(businessObject?.name || "").trim();
    if (elementName) return elementName;
    const eventDefinition = businessObject?.eventDefinitions?.[0];
    if (eventDefinition?.$type !== "bpmn:LinkEventDefinition") return "";
    return String(eventDefinition.name || "").trim();
};

export const linkNamesInDiagram = (modeler: any): string[] => {
    const elementRegistry = typeof modeler?.get === "function" ? modeler.get("elementRegistry") : null;
    const elements: any[] = typeof elementRegistry?.getAll === "function" ? elementRegistry.getAll() : [];
    const names = new Set<string>();
    elements.forEach((element) => {
        const businessObject = element?.businessObject;
        if (businessObject?.eventDefinitions?.[0]?.$type !== "bpmn:LinkEventDefinition") return;
        const name = linkNameFor(businessObject);
        if (name) names.add(name);
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
};

// PRIM-PILOT-044 (ACH-048): the throw side (error_end) writes the errorCode into the NATIVE BPMN
// structure (<bpmn:error> at the document level + errorRef on the errorEventDefinition), NOT into
// a fluss:Binding — that's exactly what Fluss::Compiler#error_code_for (compiler.rb:270-276)
// already reads via XPath over the whole document, and it is untouched by this pilot. The capture
// side (BoundaryErrorNodePanel, EventNodePanels.tsx) keeps using its own fluss:Binding
// kind="error" errorCodes CSV — the two mechanisms are deliberately NOT unified here (see plan.md
// DECISÃO 1).

// A code with a comma would be mathematically impossible to match against
// Fluss::ErrorBoundaryBinding.captures?'s `raw.split(",")` on the capture side — strip it at the
// source rather than produce an error nobody can ever catch.
export const sanitizeErrorCode = (raw: unknown): string => String(raw ?? "").replace(/,/g, "").trim();

// Climbs $parent (NOT the literal `$parent.$parent` of the updateSignalEventType precedent below)
// until it finds the real bpmn:Definitions — an error_end living inside a bpmn:SubProcess (its
// central use case: being captured by a boundary_error attached to that subprocess) would
// otherwise have its <bpmn:error> written to the wrong scope, or not written at all.
export const definitionsFor = (businessObject: any): any | null => {
    let node = businessObject?.$parent;
    while (node) {
        if (node.$type === "bpmn:Definitions") return node;
        node = node.$parent;
    }
    return null;
};

// Mirrors Fluss::Compiler#error_code_for's precedence (errorCode attribute, else id). Tolerates
// errorRef as either a resolved moddle object (the normal in-memory shape after this panel writes
// it) or a plain string id (e.g. freshly parsed XML before bpmn-js resolves references), resolving
// the latter against definitionsFor(...).rootElements.
export const errorEndCodeFor = (businessObject: any): string => {
    const eventDefinition = businessObject?.eventDefinitions?.[0];
    let ref = eventDefinition?.errorRef;
    if (!ref) return "";
    if (typeof ref === "string") {
        const definitions = definitionsFor(businessObject);
        ref = (definitions?.rootElements || []).find(
            (candidate: any) => candidate?.$type === "bpmn:Error" && candidate.id === ref
        );
    }
    if (!ref) return "";
    return String(ref.errorCode || ref.id || "");
};

// DECISÃO 2/3/4/6/7 (plan.md): reuse-by-value (case-insensitive, mirroring
// ErrorBoundaryBinding.captures?'s downcase compare) instead of the Signal precedent's
// rename-in-place -- a shared <bpmn:error> can be referenced by several error_end nodes (and by
// boundary events from standard external BPMN), so renaming it would silently change everyone
// else's semantics. An empty/blank code clears errorRef but deliberately leaves the <bpmn:error>
// element in the document (no garbage collection -- see plan.md non_goals).
export const updateErrorEndCode = (
    selection: FlussElementSelection,
    modeler: any,
    rawCode: string
): void => {
    const modeling = modeler?.get("modeling");
    const moddle = modeler?.get("moddle");
    const businessObject = selection.businessObject;
    const eventDefinition = businessObject?.eventDefinitions?.[0];
    if (!modeling || !moddle || eventDefinition?.$type !== "bpmn:ErrorEventDefinition") return;

    const definitions = definitionsFor(businessObject);
    if (!definitions) return;

    const code = sanitizeErrorCode(rawCode);
    if (!code) {
        modeling.updateModdleProperties(selection.element, eventDefinition, { errorRef: undefined });
        return;
    }

    const rootElements: any[] = definitions.rootElements || [];
    let error = rootElements.find(
        (candidate: any) =>
            candidate?.$type === "bpmn:Error" &&
            String(candidate.errorCode || candidate.id || "").toLowerCase() === code.toLowerCase()
    );

    if (!error) {
        // DECISÃO 3: prefer bpmnFactory (generates AND claims a real unique id via
        // moddle.ids.nextPrefixed); fall back to moddle.create with a collision-checked slug id
        // (and claim it too, when the moddle exposes ids.claim) for callers/tests without a
        // bpmnFactory service.
        const bpmnFactory = typeof modeler?.get === "function" ? modeler.get("bpmnFactory") : null;
        if (bpmnFactory && typeof bpmnFactory.create === "function") {
            error = bpmnFactory.create("bpmn:Error", { name: code, errorCode: code });
        } else {
            const usedIds = new Set(rootElements.map((candidate: any) => candidate?.id).filter(Boolean));
            const slug = code.replace(/[^A-Za-z0-9_]/g, "_") || selection.id;
            let id = `Error_${slug}`;
            let suffix = 1;
            while (usedIds.has(id)) {
                suffix += 1;
                id = `Error_${slug}_${suffix}`;
            }
            error = moddle.create("bpmn:Error", { id, name: code, errorCode: code });
            if (typeof moddle.ids?.claim === "function") {
                moddle.ids.claim(id, error);
            }
        }
        modeling.updateModdleProperties(selection.element, definitions, {
            rootElements: [...rootElements, error],
        });
    }

    modeling.updateModdleProperties(selection.element, eventDefinition, { errorRef: error });
};

// Aggregates candidate error codes from across the whole diagram: (a) boundary events and
// event-subprocess starts with an errorEventDefinition, reading their fluss:Binding kind="error"
// errorCodes CSV (excluding "any" and blanks); (b) other error_end nodes' resolved code; (c) every
// <bpmn:error> already declared at the document level. Case-insensitive dedupe, first-seen casing
// kept, alphabetically sorted (case-insensitive). Same defensive guard as resolveAnchorTable:
// any missing modeler/registry resolves to [].
export const collectErrorCodeSuggestions = (modeler: any, definitions?: any | null): string[] => {
    const elementRegistry = typeof modeler?.get === "function" ? modeler.get("elementRegistry") : null;
    const elements: any[] = typeof elementRegistry?.getAll === "function" ? elementRegistry.getAll() : [];
    if (elements.length === 0 && !definitions) return [];

    const seen = new Map<string, string>();
    const add = (raw: unknown) => {
        const value = String(raw || "").trim();
        if (!value || value.toLowerCase() === "any") return;
        const key = value.toLowerCase();
        if (!seen.has(key)) seen.set(key, value);
    };

    for (const element of elements) {
        const businessObject = element?.businessObject;
        if (!businessObject) continue;
        const definitionType = businessObject?.eventDefinitions?.[0]?.$type;
        if (definitionType !== "bpmn:ErrorEventDefinition") continue;

        // Element kind is read from `element.type` (the bpmn-js Shape's own type, same field
        // panelNodeKindFor/resolveOutcomeVocabulary key off elsewhere in this file), NOT
        // businessObject.$type -- test fixtures across this module build businessObject without
        // ever setting $type, mirroring how selections are constructed from real elementRegistry
        // entries throughout the codebase.
        const elementType = element?.type;
        const isBoundaryOrEventSubprocessStart =
            elementType === "bpmn:BoundaryEvent" ||
            (elementType === "bpmn:StartEvent" &&
                businessObject.$parent?.$type === "bpmn:SubProcess" &&
                businessObject.$parent?.triggeredByEvent);

        if (isBoundaryOrEventSubprocessStart) {
            const binding = bindingForKind(businessObject, "error");
            String(binding?.errorCodes || "").split(",").forEach((code) => add(code));
        } else if (elementType === "bpmn:EndEvent") {
            add(errorEndCodeFor(businessObject));
        }
    }

    for (const error of definitions?.rootElements || []) {
        if (error?.$type === "bpmn:Error") add(error.errorCode || error.id);
    }

    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
};

export const escalationCodeFor = (businessObject: any): string => {
    const eventDefinition = businessObject?.eventDefinitions?.[0];
    let ref = eventDefinition?.escalationRef;
    if (typeof ref === "string") {
        ref = (definitionsFor(businessObject)?.rootElements || []).find(
            (candidate: any) => candidate?.$type === "bpmn:Escalation" && candidate.id === ref
        );
    }
    return ref ? String(ref.escalationCode || ref.id || "") : "";
};

export const updateEscalationCode = (selection: FlussElementSelection, modeler: any, rawCode: string): void => {
    const modeling = modeler?.get("modeling");
    const moddle = modeler?.get("moddle");
    const eventDefinition = selection.businessObject?.eventDefinitions?.[0];
    const definitions = definitionsFor(selection.businessObject);
    if (!modeling || !moddle || !definitions || eventDefinition?.$type !== "bpmn:EscalationEventDefinition") return;

    const code = String(rawCode || "").trim();
    if (!code) {
        modeling.updateModdleProperties(selection.element, eventDefinition, { escalationRef: undefined });
        return;
    }
    const rootElements: any[] = definitions.rootElements || [];
    let escalation = rootElements.find((candidate: any) =>
        candidate?.$type === "bpmn:Escalation" &&
        String(candidate.escalationCode || candidate.id || "").toLowerCase() === code.toLowerCase()
    );
    if (!escalation) {
        const bpmnFactory = modeler?.get("bpmnFactory");
        if (typeof bpmnFactory?.create === "function") {
            escalation = bpmnFactory.create("bpmn:Escalation", { name: code, escalationCode: code });
        } else {
            const usedIds = new Set(rootElements.map((candidate: any) => candidate?.id).filter(Boolean));
            const slug = code.replace(/[^A-Za-z0-9_]/g, "_") || selection.id;
            let id = `Escalation_${slug}`;
            let suffix = 1;
            while (usedIds.has(id)) id = `Escalation_${slug}_${++suffix}`;
            escalation = moddle.create("bpmn:Escalation", { id, name: code, escalationCode: code });
            moddle.ids?.claim?.(id, escalation);
        }
        modeling.updateModdleProperties(selection.element, definitions, { rootElements: [...rootElements, escalation] });
    }
    modeling.updateModdleProperties(selection.element, eventDefinition, { escalationRef: escalation });
};

export const collectEscalationCodeSuggestions = (modeler: any, definitions?: any | null): string[] => {
    const registry = modeler?.get("elementRegistry");
    const elements: any[] = registry?.getAll?.() || [];
    const seen = new Map<string, string>();
    const add = (raw: unknown) => {
        const value = String(raw || "").trim();
        if (value && !seen.has(value.toLowerCase())) seen.set(value.toLowerCase(), value);
    };
    elements.forEach((element) => {
        if (element?.businessObject?.eventDefinitions?.[0]?.$type === "bpmn:EscalationEventDefinition") {
            add(escalationCodeFor(element.businessObject));
        }
    });
    (definitions?.rootElements || []).forEach((element: any) => {
        if (element?.$type === "bpmn:Escalation") add(element.escalationCode || element.id);
    });
    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
};

export const ensureBinding = (
    selection: FlussElementSelection,
    modeler: any,
    kind: string
): FlussBindingLike | null => {
    const modeling = modeler?.get("modeling");
    const moddle = modeler?.get("moddle");
    if (!modeling || !moddle) return null;

    const businessObject = selection.businessObject;
    let extensionElements = businessObject.extensionElements;
    if (!extensionElements) {
        extensionElements = moddle.create("bpmn:ExtensionElements", { values: [] });
        modeling.updateProperties(selection.element, { extensionElements });
    }

    let binding = bindingForKind(businessObject, kind);
    if (!binding && ["assignment", "approval", "service"].includes(kind)) {
        binding = primaryBindingFor(businessObject);
    }
    if (!binding) {
        const created = moddle.create("fluss:Binding", { kind }) as FlussBindingLike;
        created.$parent = extensionElements;
        modeling.updateModdleProperties(selection.element, extensionElements, {
            values: [...(extensionElements.values || []), created],
        });
        binding = created;
    }
    if (!binding) return null;
    if (binding.kind !== kind) {
        modeling.updateModdleProperties(selection.element, binding, { kind });
    }
    return binding;
};

export const updateBinding = (
    selection: FlussElementSelection,
    modeler: any,
    kind: string,
    updates: Record<string, unknown>
) => {
    const modeling = modeler?.get("modeling");
    const binding = ensureBinding(selection, modeler, kind);
    if (!modeling || !binding) return;
    const normalized = Object.fromEntries(
        Object.entries(updates).map(([key, value]) => [
            key,
            value === "" || value === null || value === undefined ? undefined : String(value),
        ])
    );
    modeling.updateModdleProperties(selection.element, binding, normalized);
};

export const removeBindingKind = (
    selection: FlussElementSelection,
    modeler: any,
    kind: string
) => {
    const modeling = modeler?.get("modeling");
    const extensionElements = selection.businessObject?.extensionElements;
    if (!modeling || !extensionElements) return;
    const values = (extensionElements.values || []).filter(
        (value: FlussBindingLike) => !(value.$type === "fluss:Binding" && value.kind === kind)
    );
    modeling.updateModdleProperties(selection.element, extensionElements, { values });
};

export const parseMapping = (raw: unknown): FieldMapping[] => {
    if (!raw) return [];
    try {
        const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return [];
        return Object.entries(parsed as Record<string, FlussValue>).map(([target, source]) => ({
            target,
            source,
        }));
    } catch (_error) {
        return [];
    }
};

export const serializeMapping = (rows: FieldMapping[]) => {
    const entries = rows.filter((row) => row.target && row.source !== undefined);
    if (entries.length === 0) return "";
    return JSON.stringify(Object.fromEntries(entries.map((row) => [row.target, row.source])));
};

// PRIM-022 (round 6): carries the real sys_column through (see ScopeOption.column) so the DataPicker
// can delegate "Enter fixed value" to ValueField/FIELD_COMPONENT_MAP for REFERENCE/CHOICE/CURRENCY
// instead of a generic text box. Requires metadata_controller.rb's sys_columns whitelist to expose
// reference_filter/column_subtype/depends_on_column_id/currency_code/currency_decimal_places/
// default_value (round 6 change) — records fetched before that change simply won't have them, and
// this falls back to "" / null, same as the FlussConditionBuilder.syntheticColumn precedent.
export const metadataToScopeOptions = (records: any[] = []): ScopeOption[] =>
    records.map((record, index) => ({
        name: String(record.name || ""),
        label: String(record.label || record.name || ""),
        type: String(record.column_type || "TEXT").toUpperCase(),
        required: Boolean(record.is_mandatory),
        referencedTableName: record.referenced_table_name
            ? String(record.referenced_table_name)
            : undefined,
        // PRIM-010: keep the real sys_columns.id so CHOICE/REFERENCE synthetic columns built for
        // the Fluss condition builder can actually resolve options/lookups against it.
        id: record.id ? String(record.id) : undefined,
        column: record.id ? new SysColumn({
            id: String(record.id),
            sys_table_id: String(record.sys_table_id || ""),
            active: true,
            label: String(record.label || record.name || ""),
            name: String(record.name || ""),
            column_type: String(record.column_type || "TEXT").toUpperCase(),
            column_subtype: String(record.column_subtype || ""),
            order: index,
            referenced_table_name: record.referenced_table_name ? String(record.referenced_table_name) : "",
            reference_filter: record.reference_filter ? String(record.reference_filter) : "",
            currency_code: record.currency_code ? String(record.currency_code) : "",
            currency_decimal_places: typeof record.currency_decimal_places === "number"
                ? record.currency_decimal_places
                : null,
            default_value: record.default_value ? String(record.default_value) : "",
            is_display: false,
            is_hidden: false,
            is_mandatory: Boolean(record.is_mandatory),
            depends_on_column_id: record.depends_on_column_id ? String(record.depends_on_column_id) : "",
            created_at: new Date(0),
            updated_at: new Date(0),
        }) : undefined,
    }));

// PRIM-PILOT-023: mirrors WorkRecordMaterializer#validate_mandatory_columns!,
// which drops the model primary key for create targets.
const SYSTEM_GENERATED_TARGET_COLUMNS = new Set(["id"]);

export const metadataToCreatableScopeOptions = (records: any[] = []): ScopeOption[] =>
    metadataToScopeOptions(records).filter((option) => !SYSTEM_GENERATED_TARGET_COLUMNS.has(option.name));

// PRIM-017/018: the "Record" list every node panel offers must reflect the table the process is
// actually anchored to (the Start's trigger binding), not a hardcoded "task". Walks the registry
// since the anchor may live on a node other than the one
// currently selected. Returns "" (not "task") when there is no trigger table yet, so callers can
// tell "no anchor" apart from "the anchor really is task" and choose their own fallback.
export const resolveAnchorTable = (modeler: any): string => {
    const elementRegistry = modeler?.get("elementRegistry");
    const elements: any[] =
        typeof elementRegistry?.getAll === "function" ? elementRegistry.getAll() : [];
    for (const element of elements) {
        const businessObject = element?.businessObject;
        if (!businessObject || businessObject.$type !== "bpmn:StartEvent") continue;
        const parent = businessObject.$parent;
        if (parent?.$type === "bpmn:SubProcess" && parent?.triggeredByEvent) continue;
        const binding = bindingForKind(businessObject, "trigger");
        const table = String(binding?.table || "");
        if (table) return table;
    }
    return "";
};

export const durationToMinutes = (amount: number | null, unit: string) => {
    if (amount === null || Number.isNaN(amount)) return "";
    if (unit === "days") return String(amount * 1440);
    if (unit === "hours" || unit === "business_hours") return String(amount * 60);
    return String(amount);
};

export const minutesToDuration = (raw: unknown) => {
    const minutes = Number(raw || 0);
    if (minutes > 0 && minutes % 1440 === 0) {
        return { amount: minutes / 1440, unit: "days" as const };
    }
    if (minutes > 0 && minutes % 60 === 0) {
        return { amount: minutes / 60, unit: "hours" as const };
    }
    return { amount: raw ? minutes : null, unit: "minutes" as const };
};

export const bindingTechnicalRows = (businessObject: any) =>
    bindingsFor(businessObject).flatMap((binding) =>
        Object.entries(binding)
            .filter(([key, value]) => !key.startsWith("$") && value !== undefined && value !== "")
            .map(([key, value]) => ({
                key: binding.kind ? `${binding.kind}.${key}` : key,
                value: typeof value === "object" ? JSON.stringify(value) : String(value),
            }))
    );
